import React, { useState, useEffect } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { blueGrey } from "@mui/material/colors";
import { SUPPORTED_CURRENCIES } from '../../../constants';
import { useTranslation } from 'react-i18next';
import { SelectWithAlert } from '@/components/ui/select-with-alert';
import { InputWithAlert } from '@/components/ui/input-with-alert';

import { AlertTitle, Alert } from '@/components/ui/alert';
import { AlertCircle } from "lucide-react";
import { Button } from '@/components/ui/button';
import { electronicFormatIBAN, isValidIBAN } from 'ibantools';
import {  avalibleBanks, getBankFromSwiftCode, getSwiftCodeForBank, PKO } from '@/bank_constants';
const grayLight = blueGrey[700];

const INCUBATOR_TYPE = 'INCUBATOR'
const CLIENT_TYPE = 'CLIENT'

const ClientBankAccount = (props) => {

  const { t } = useTranslation();
  const { isIncubator } = props

  const [error, setError] = useState();

  const isBankCurrencyComboExists = (currency, swift, accountId) => {
    return props.bankAccounts.some((account) =>
      account.currency === currency &&
      account.swift === swift &&
      account.id !== accountId
    );
  };

  const isIbanAlreadyExists = (iban, accountId) => {
    if (!iban || iban.length === 0) return false;
    const normalizedIban = electronicFormatIBAN(iban);
    return props.bankAccounts.some((account) =>
      account.account &&
      electronicFormatIBAN(account.account) === normalizedIban &&
      account.id !== accountId
    );
  };

  // Function to check if any account has validation errors
  const hasValidationErrors = () => {
    return props.bankAccounts.some(account => {
      if (account.account && account.account.length > 0) {
        const isValid = isValidIBAN(electronicFormatIBAN(account.account));
        if (!isValid){
          setError("Invalid iban")
          return true;
        }
        if (isIbanAlreadyExists(account.account, account.id)) return true;
      }

      if (account.currency && account.swift &&
          isBankCurrencyComboExists(account.currency, account.swift, account.id)) {
        return true;
      }

      return false;
    });
  };

  // Expose validation function to parent if callback provided
  useEffect(() => {
    if (props.onValidationChange) {
      props.onValidationChange(hasValidationErrors());
    }
  }, [props.bankAccounts, error]);


  const handleAddAccount = () => {
    const newId = props.bankAccounts.length + 1;
    const defaultCurrency = 'PLN';
    const defaultBank = PKO;

    // Find available bank+currency combinations
    const availableCurrencies = SUPPORTED_CURRENCIES.map((currency) => currency.value);
    const availableBankOptions = avalibleBanks.map((bank) => bank.value);

    let foundAvailableCombo = false;
    let newCurrency = defaultCurrency;
    let newBank = defaultBank;

    // Try to find an available bank+currency combination
    for (const bank of availableBankOptions) {
      const bankSwift = getSwiftCodeForBank(bank);
      for (const currency of availableCurrencies) {
        if (!isBankCurrencyComboExists(currency, bankSwift, -1)) {
          newCurrency = currency;
          newBank = bank;
          foundAvailableCombo = true;
          break;
        }
      }
      if (foundAvailableCombo) break;
    }

    if (!foundAvailableCombo) {
      setError(t('alerts:error_client_bank_account_no_free_bank_currency_combination'));
      return;
    }

    props.setBankAccounts([...props.bankAccounts, {
      id: newId,
      account: '',
      currency: newCurrency,
      swift: getSwiftCodeForBank(newBank),
      accountType: isIncubator ? INCUBATOR_TYPE : CLIENT_TYPE
    }]);
    setError(undefined);
  };

  const handleRemoveAccount = (id) => {
    const updatedAccounts = props.bankAccounts.filter((acc) => acc.id !== id);
    props.setBankAccounts(updatedAccounts);
  };

  const handleCurrencyChange = (id, newCurrency) => {
    const account = props.bankAccounts.find(acc => acc.id === id);
    if (isBankCurrencyComboExists(newCurrency, account.swift, id)) {
      setError(t('alerts:error_client_bank_account_bank_currency_already_declared'));
      return;
    }

    const updatedAccounts = props.bankAccounts.map((acc) =>
      acc.id === id ? { ...acc, currency: newCurrency } : acc
    );
    props.setBankAccounts(updatedAccounts);
    setError(undefined);
  };


    const handleBankChange = (id, bank) => {
      const account = props.bankAccounts.find(acc => acc.id === id);
      const newSwift = getSwiftCodeForBank(bank);

      if (isBankCurrencyComboExists(account.currency, newSwift, id)) {
        setError(t('alerts:error_client_bank_account_bank_currency_already_declared'));
        return;
      }

      const updatedAccounts = props.bankAccounts.map((acc) =>
        acc.id === id ? { ...acc, swift: newSwift } : acc
      );
      props.setBankAccounts(updatedAccounts);
      setError(undefined);
    };


  const handleAccountChange = (id, newValue) => {
    const isValid = isValidIBAN(electronicFormatIBAN(newValue));

    // Check for duplicate IBAN BEFORE updating state
    if (isValid && newValue.length > 0 && isIbanAlreadyExists(newValue, id)) {
      // Clear the field and show error
      const updatedAccounts = props.bankAccounts.map((account) =>
        account.id === id ? { ...account, account: '' } : account
      );
      props.setBankAccounts(updatedAccounts);
      setError(t('alerts:error_client_bank_account_iban_already_exists'));
      return;
    }

    // Update the account normally
    const updatedAccounts = props.bankAccounts.map((account) =>
      account.id === id ? { ...account, account: newValue } : account
    );
    props.setBankAccounts(updatedAccounts);

    // Clear any existing error first, then check for new errors
    setError(undefined);

    // Check for format errors only after clearing previous errors
    if (!isValid && newValue.length !== 0) {
      setError(t('alerts:error_client_bank_account_type'));
    }
  };

  return (
    <div className='w-full '>
      {props.bankAccounts && props.bankAccounts.map((account, index) => (
        <div key={index} className='w-full'>
          <div className='w-full space-y-4'>
            <div className='grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-3 items-end'>
              <div className="md:col-span-6">
                <InputWithAlert
                  value={account.account}
                  onChange={(e) => handleAccountChange(account.id, e.target.value)}
                  label={isIncubator ? t('clients:bank_account_inqubator') : t('clients:bank_account_client')}
                  error={false}
                  errorMessage={"te"}
                  inputClassName={'h-12 mt-2 mb-4  bg-white'}
                />
              </div>
              <div className="md:col-span-3">
                <div className="block md:hidden">
                  <label className="block text-sm font-medium text-gray-700 pb-1">{t('common:currency') || 'Currency'}</label>
                </div>
                <SelectWithAlert
                  value={account.currency}
                  options={SUPPORTED_CURRENCIES}
                  onChangeValue={(e) => handleCurrencyChange(account.id, e)}
                  inputClassName={'h-12 mt-2 mb-4  bg-white'}
                />
              </div>

              <div className="md:col-span-3">
                <div className="block md:hidden">
                  <label className="block text-sm font-medium text-gray-700 pb-1">{t('clients:bank') || 'Bank'}</label>
                </div>
                <SelectWithAlert
                value={getBankFromSwiftCode(account.swift)}
                options={avalibleBanks}
                onChangeValue={(e) => handleBankChange(account.id, e)}
                inputClassName={'h-12 mt-2 mb-4  bg-white'}
                />
              </div>
            </div>
            {index !== 0 && (
              <div className="flex justify-end">
                <Button className="bg-inherit hover:bg-inherit shadow-none text-red-500 hover:text-red-700" onClick={() => handleRemoveAccount(account.id)}>
                  <DeleteIcon style={{ color: `${grayLight}` }} />
                  <span className="ml-2 md:hidden">Remove Account</span>
                </Button>
              </div>
            )}
          </div>
        </div>

      ))}
      <Button className="mt-4" onClick={handleAddAccount}>
        {t('clients:create_client_create_payment_account_button')}
      </Button>
      {error && (<div className="mt-4">
        <Alert variant="destructive" className="relative mb-2 h-12 flex items-center justify-start !pl-[0.75rem]">
          <AlertCircle className="h-4 w-4 !static !mr-2" />
          <AlertTitle className="m-0 !p-0">
            {error}
          </AlertTitle>
        </Alert>
      </div>)
      }
    </div>
  );
};

export default ClientBankAccount;
